import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { playSiren } from "@/lib/sound";
import { Siren, ShieldAlert, VolumeX, Volume2, Minus, ChevronUp } from "lucide-react";

const MUTE_KEY = "homiie_admin_siren_muted";

// Small floating admin alert for Emergency SOS + Safety cases: siren loop, mute and minimize.
export function EmergencyAlarm() {
    const [data, setData] = useState(null);
    const [muted, setMuted] = useState(localStorage.getItem(MUTE_KEY) === "1");
    const [min, setMin] = useState(false);
    const nav = useNavigate();
    const timer = useRef(null);
    const lastKey = useRef("");

    useEffect(() => {
        const load = () => api.get("/admin/emergencies-summary").then((r) => setData(r.data)).catch(() => {});
        load();
        const t = setInterval(load, 10000);
        window.addEventListener("homiie-emergency-updated", load);
        return () => { clearInterval(t); window.removeEventListener("homiie-emergency-updated", load); };
    }, []);

    const unack = (data?.unacknowledged || 0) + (data?.safety_new || 0);
    const key = `${data?.unacknowledged}-${data?.safety_new}-${data?.first?.case_id}-${data?.safety_first?.case_id}`;
    useEffect(() => {
        if (key !== lastKey.current && unack > 0) setMin(false);
        lastKey.current = key;
    }, [key, unack]);
    useEffect(() => {
        clearInterval(timer.current);
        if (unack > 0 && !muted) {
            playSiren();
            timer.current = setInterval(playSiren, 6000);
        }
        return () => { clearInterval(timer.current); };
    }, [unack, muted]);

    const total = (data?.active || 0) + (data?.safety_active || 0);
    if (!total) return null;
    const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem(MUTE_KEY, m ? "1" : "0"); };

    if (min) {
        return (
            <>
            <div data-testid="alarm-spacer" aria-hidden className="h-16" />
            <button data-testid="alert-popup-restore" onClick={() => setMin(false)} className={`fixed bottom-5 left-5 z-40 flex items-center gap-2 rounded-full lg:left-[276px] px-4 py-2.5 text-sm font-extrabold text-white shadow-xl ${unack ? "animate-pulse bg-red-600" : "bg-red-500"}`}>
                <Siren size={16} /> {unack ? `${unack} new` : `${total} open`} alert{(unack || total) > 1 ? "s" : ""} <ChevronUp size={15} />
            </button>
            </>
        );
    }
    const e = data.first;
    const s = data.safety_first;
    return (
        <>
        <div data-testid="alarm-spacer" aria-hidden className="h-56" />
        <div data-testid="emergency-alarm-banner" className="fixed bottom-5 left-5 z-40 w-[340px] lg:left-[276px] max-w-[calc(100vw-2.5rem)] overflow-hidden rounded-2xl bg-white shadow-2xl ring-2 ring-red-500">
            <div className={`flex items-center gap-2 px-4 py-2.5 text-white ${unack ? "animate-pulse bg-red-600" : "bg-red-500"}`}>
                <Siren size={18} />
                <p className="flex-1 text-sm font-extrabold">{unack ? `${unack} new alert${unack > 1 ? "s" : ""} — respond now` : `${total} case(s) in progress`}</p>
                <button data-testid="alert-popup-mute" onClick={toggleMute} title={muted ? "Unmute siren" : "Mute siren"} className="rounded-full bg-white/20 p-1.5">{muted ? <VolumeX size={14} /> : <Volume2 size={14} />}</button>
                <button data-testid="alert-popup-minimize" onClick={() => setMin(true)} title="Minimize" className="rounded-full bg-white/20 p-1.5"><Minus size={14} /></button>
            </div>
            <div className="space-y-2 p-3">
                {data.active > 0 && (
                    <button data-testid="alert-popup-emergency" onClick={() => nav("/admin/emergencies")} className="flex w-full items-start gap-2.5 rounded-xl bg-red-50 p-2.5 text-left">
                        <Siren size={16} className="mt-0.5 shrink-0 text-red-600" />
                        <span className="text-xs text-navy"><b className="block text-red-700">Emergency SOS · {data.active} active{data.unacknowledged ? ` (${data.unacknowledged} new)` : ""}</b>{e && `${e.case_id} · ${e.member?.name || ""} · ${e.customer?.phone || ""}`}</span>
                    </button>
                )}
                {data.safety_active > 0 && (
                    <button data-testid="alert-popup-safety" onClick={() => nav("/admin/safety")} className="flex w-full items-start gap-2.5 rounded-xl bg-amber-50 p-2.5 text-left">
                        <ShieldAlert size={16} className="mt-0.5 shrink-0 text-amber-600" />
                        <span className="text-xs text-navy"><b className="block text-amber-700">Safety cases · {data.safety_active} open{data.safety_new ? ` (${data.safety_new} new)` : ""}</b>{s && `${s.case_id} · ${s.category} · ${s.severity}`}</span>
                    </button>
                )}
            </div>
        </div>
        </>
    );
}
